import React, { useState } from 'react';
import { Button, Input, Modal, Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import type { OrderStockLine } from '../../api/orderStockStatusApi';
import type { OrderProduct } from '../../../../types/order';
import ConfirmStockTable from './ConfirmStockTable';
import './OrderGuide.css';

export type OrderFulfillmentSource = 'WAREHOUSE' | 'FACTORY_DIRECT';

/** „Skąd realizujemy?” of the confirmation window (owner 2026-10-05). */
export interface OrderFulfillmentChoice {
  source: OrderFulfillmentSource;
  note: string;
}

export const DEFAULT_FULFILLMENT_CHOICE: OrderFulfillmentChoice = { source: 'WAREHOUSE', note: '' };
export const MAX_FULFILLMENT_NOTE_LENGTH = 500;

/**
 * „Skąd realizujemy?”: one of two sources (warehouse by default, straight from
 * the factory to the customer) and an optional message to Logistyk (Valentyna).
 * „Zobacz stan magazynu” opens the stock of the order's products in a separate
 * window. Information only — the choice sets the quantity of the draft ZD and
 * is named in Valentyna's draft and e-mail; it changes no cost, FIFO or WZ.
 */
export default function ConfirmFulfillmentBlock({
  orderNumber,
  lines,
  products,
  value,
  onChange,
}: {
  orderNumber: string;
  lines: OrderStockLine[];
  /** Order lines, for the dimensions under the product names. */
  products?: OrderProduct[];
  value: OrderFulfillmentChoice;
  onChange: (value: OrderFulfillmentChoice) => void;
}) {
  const { t } = useTranslation(['orders', 'common']);
  const [stockOpen, setStockOpen] = useState(false);
  const option = (source: OrderFulfillmentSource, title: string, hint: string, extra?: React.ReactNode) => (
    <label className={`zk-confirm__person${value.source === source ? ' is-checked' : ''}`} data-testid={`confirmation-fulfillment-${source}-card`}>
      <Radio value={source} aria-label={title} data-testid={`confirmation-fulfillment-${source}`} />
      <span>
        <b>{title}</b>
        <small>{hint}</small>
        {extra}
      </span>
    </label>
  );
  return (
    <div data-testid="confirmation-fulfillment">
      <div className="zk-confirm__sect">{t('orders:process.confirmDialog.fulfillmentTitle')}</div>
      <Radio.Group
        value={value.source}
        onChange={(event) => onChange({ ...value, source: event.target.value as OrderFulfillmentSource })}
        className="zk-confirm__people"
      >
        {option(
          'WAREHOUSE',
          t('orders:process.confirmDialog.fulfillmentWarehouse'),
          t('orders:process.confirmDialog.fulfillmentWarehouseHint'),
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            data-testid="confirmation-fulfillment-view-stock"
            onClick={(event) => { event.preventDefault(); event.stopPropagation(); setStockOpen(true); }}
          >
            {t('orders:process.confirmDialog.viewStock')}
          </Button>,
        )}
        {option(
          'FACTORY_DIRECT',
          t('orders:process.confirmDialog.fulfillmentFactory'),
          t('orders:process.confirmDialog.fulfillmentFactoryHint'),
        )}
      </Radio.Group>
      <label htmlFor="confirmation-fulfillment-note" className="zk-confirm__note" style={{ display: 'block', marginTop: 8 }}>
        {t('orders:process.confirmDialog.fulfillmentNoteLabel')}
      </label>
      <Input.TextArea
        id="confirmation-fulfillment-note"
        data-testid="confirmation-fulfillment-note"
        rows={2}
        maxLength={MAX_FULFILLMENT_NOTE_LENGTH}
        showCount
        value={value.note}
        placeholder={t('orders:process.confirmDialog.fulfillmentNotePlaceholder')}
        onChange={(event) => onChange({ ...value, note: event.target.value })}
      />
      <div className="zk-confirm__note">{t('orders:process.confirmDialog.fulfillmentInfo')}</div>
      <Modal
        open={stockOpen}
        title={t('orders:process.confirmDialog.stockModalTitle', { orderNumber })}
        footer={<Button onClick={() => setStockOpen(false)} data-testid="confirmation-fulfillment-stock-close">{t('common:close')}</Button>}
        onCancel={() => setStockOpen(false)}
        width={640}
        destroyOnClose
      >
        <ConfirmStockTable lines={lines} products={products} testId="zk-stock-modal-table" />
        <div className="zk-confirm__note">{t('orders:process.confirmDialog.stockNote')}</div>
      </Modal>
    </div>
  );
}
